import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ConfirmationDialog, IconNames } from '@dotcom/crown-ui';
import { useAppContext } from '../../../context/app-context';
import { useCardContext } from '../../../context/card-context';
import { useCropContext } from '../../../context/crop-context';
import { useInitializationDataContext } from '../../../context/data-context';
import { useActiveCanvas } from '../../../hooks/use-active-canvas';
import { useFeatureFlags } from '../../../hooks/use-feature-flags';
import { useIsPodProductCode } from '../../../hooks/use-is-pod-product-code';
import { useIsSignAndSend } from '../../../hooks/use-is-sign-and-send';
import { useUIStore } from '../../../stores/ui';
import { CanvasDataTypes } from '../../../utils';
import { Drawer } from '../drawer/drawer';
import { DrawerPositions } from '../drawer/drawer-types';
import { ToolbarAction, ToolbarActionMenu, useToolbarExitAnimation, useToolbarPosition } from '../toolbar-action-menu';
import { InlineToolBody } from './fragments';
import { useCachedActiveImage } from './hooks/use-cached-active-image';
import { useReplaceImageFlow } from './hooks/use-replace-image-flow';
import { getImageEditToolbarHost } from './image-edit-toolbar-host';
import { ImageEditToolbarProps, InlineTool } from './image-edit-toolbar-types';

const TOOLBAR_TEST_ID_PREFIX = 'image-edit-toolbar';

const translateOrFallback = (value: unknown, fallback: string): string =>
  typeof value === 'string' ? value : fallback;

export const ImageEditToolbar = ({
  isHandwriting,
  onCrop,
  onDelete,
  onReset,
}: ImageEditToolbarProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const { appState } = useAppContext();
  const { cardState } = useCardContext();
  const { activeCardIndex } = cardState;
  const canvas = useActiveCanvas();
  const { isCropping, cancelCropping } = useCropContext();
  const isPodProductCode = useIsPodProductCode();
  const isSignAndSend = useIsSignAndSend();
  const { IS_FORCE_FILL_ENABLED } = useFeatureFlags();
  const {
    initializedDataState: { isUS },
  } = useInitializationDataContext();

  // Open/close state and active inline tool live in the zustand UI store,
  // not in app-context. The toolbar stays mounted while either is truthy so
  // that transient canvas selection clears don't dismiss the editor.
  const isToolbarOpen = useUIStore((state) => state.isImageEditToolbarOpen);
  const activeTool = useUIStore((state) => state.imageEditToolbarActiveTool) as InlineTool | null;
  const closeImageEditToolbar = useUIStore((state) => state.closeImageEditToolbar);
  const setActiveTool = useUIStore((state) => state.setImageEditToolbarActiveTool);

  const isMobile = Boolean(appState.isMobileApp);
  const shouldShowReplace = IS_FORCE_FILL_ENABLED && activeCardIndex === 0;
  const isPhotoZoneImage = canvas?.current?.getActiveObject()?.data?.type === CanvasDataTypes.PhotoZoneImage;

  // Mirror the legacy ImageEditDrawer's `isOpen={... || isCropping}` so that
  // canvas-level deselect handlers can't dismiss the toolbar while the user
  // is inside the crop flow.
  const isVisible = isToolbarOpen || activeTool !== null || isCropping;

  // Desktop exit animation — keeps the panel mounted while the slide-out plays.
  const {
    isRendered: isDesktopRendered,
    isClosing: isDesktopClosing,
    onExitComplete: onDesktopExitComplete,
  } = useToolbarExitAnimation(!isMobile && isVisible);

  // Align toolbar top with the canvas wrapper's top edge.
  useToolbarPosition({
    getHost: getImageEditToolbarHost,
    getAnchor: () => canvas?.current?.getElement()?.closest<HTMLElement>('[data-testid^="canvas-wrapper-"]') ?? null,
    enabled: !isMobile && isVisible,
    measurementKey: activeCardIndex,
  });

  const { isReplaceConfirmationOpen, openReplaceConfirmation, closeReplaceConfirmation, confirmReplaceImage } =
    useReplaceImageFlow();

  // Cache the most recently selected image so inline tool buttons can still
  // act on a valid target when canvas focus is momentarily lost.
  const cachedActiveImageRef = useCachedActiveImage({
    canvas,
    enabled: isVisible,
  });

  // Synchronize `activeTool` with the crop context. Crop is driven by
  // `useCropContext` (canvas state), so this effect keeps the toolbar UI in
  // step with it: when cropping starts (from any entry point) we promote
  // `activeTool` to `'crop'` so the Apply/Back body renders; when cropping
  // ends we clear it so the menu re-appears. We also clear when the toolbar
  // becomes globally hidden so it re-opens on the menu view next time.
  useEffect(() => {
    if (!isVisible) {
      setActiveTool(null);
      return;
    }
    if (isCropping && activeTool !== 'crop') {
      setActiveTool('crop');
    } else if (!isCropping && activeTool === 'crop') {
      setActiveTool(null);
    }
  }, [activeTool, isCropping, isVisible, setActiveTool]);

  const handleClose = useCallback(() => {
    if (activeTool === 'crop') {
      cancelCropping();
    }
    setActiveTool(null);
    closeImageEditToolbar();
    canvas?.current?.discardActiveObject();
    canvas?.current?.requestRenderAll();
  }, [activeTool, cancelCropping, canvas, closeImageEditToolbar, setActiveTool]);

  // Close the toolbar (from any state) when the user presses Escape.
  // Provides a direct-close affordance in sub-panels where the X is hidden,
  // and matches the universal modal/menu dismissal shortcut.
  // Must also cancel cropping and clear `activeTool`, otherwise `isVisible`
  // (which OR-includes `activeTool !== null` and `isCropping`) would keep
  // the panel mounted.
  useEffect(() => {
    if (!isVisible) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, handleClose]);

  const restoreCachedSelection = useCallback(() => {
    const currentCanvas = canvas?.current;
    const cached = cachedActiveImageRef.current;
    const active = currentCanvas?.getActiveObject();

    if (active || !currentCanvas || !cached) {
      return;
    }

    if (currentCanvas.getObjects().includes(cached)) {
      currentCanvas.setActiveObject(cached);
      currentCanvas.requestRenderAll();
    }
  }, [canvas, cachedActiveImageRef]);

  // While an inline tool is active, if fabric clears the selection, restore
  // it immediately so the slider's `usePhotoControls` keeps a live target.
  useEffect(() => {
    const currentCanvas = canvas?.current;
    if (!activeTool || !currentCanvas || typeof currentCanvas.on !== 'function') {
      return undefined;
    }
    const onCleared = () => {
      // Zustand store updates synchronously, but React re-renders (and removes
      // this listener) are batched. Guard against restoring when the toolbar
      // was already closed — e.g. handleClose or card-face.tsx called
      // closeImageEditToolbar() before discardActiveObject() fired, so the
      // store is already up-to-date even though this listener is still live.
      const { isImageEditToolbarOpen, imageEditToolbarActiveTool } = useUIStore.getState();
      if (!isImageEditToolbarOpen && imageEditToolbarActiveTool === null) {
        return;
      }
      restoreCachedSelection();
    };
    currentCanvas.on('selection:cleared', onCleared);
    return () => {
      if (typeof currentCanvas.off === 'function') {
        currentCanvas.off('selection:cleared', onCleared);
      }
    };
  }, [activeTool, canvas, restoreCachedSelection]);

  const openInlineTool = useCallback(
    (tool: Exclude<InlineTool, 'crop'>) => {
      restoreCachedSelection();
      setActiveTool(tool);
    },
    [setActiveTool, restoreCachedSelection],
  );

  const handleBack = useCallback(() => {
    // Crop is driven by the crop context, so clearing `activeTool` alone
    // doesn't cancel it — the sync effect would immediately re-set it to
    // 'crop' while `isCropping` is still true. Cancel cropping explicitly.
    if (activeTool === 'crop') {
      cancelCropping();
      return;
    }
    if (activeTool) {
      setActiveTool(null);
      return;
    }
    // On the menu view "Back" has nowhere to go, so behave like Close.
    handleClose();
  }, [activeTool, cancelCropping, handleClose, setActiveTool]);

  const labels = {
    toolbar: translateOrFallback(t('imageEditDrawer.photoEditorDrawer'), 'Photo editor drawer'),
    back: translateOrFallback(t('imageEditDrawer.back'), 'Back'),
    close: translateOrFallback(t('imageEditDrawer.close'), 'Close'),
    replaceTitle: translateOrFallback(t('imageEditDrawer.replaceConfirmationTitle'), 'Replace image?'),
    replaceCancel: translateOrFallback(t('imageEditDrawer.replaceConfirmationCancelText'), 'Cancel'),
    replaceConfirm: translateOrFallback(t('imageEditDrawer.replaceConfirmationConfirmText'), 'Replace'),
  };

  // Image-edit specific list of menu actions, gated by feature flags and
  // canvas state. Empty while cropping (the menu is suppressed).
  const actions = useMemo<ToolbarAction[]>(() => {
    if (isCropping) {
      return [];
    }
    const list: ToolbarAction[] = [];
    if (!shouldShowReplace) {
      list.push({
        id: 'crop',
        label: 'Crop',
        icon: IconNames.CropBold,
        onClick: () => {
          onCrop();
          setActiveTool('crop');
        },
      });
    }
    list.push({ id: 'scale', label: 'Zoom', icon: IconNames.ScaleRegular, onClick: () => openInlineTool('scale') });
    list.push({ id: 'rotate', label: 'Rotate', icon: IconNames.RotateBold, onClick: () => openInlineTool('rotate') });
    if (isPodProductCode && !isPhotoZoneImage) {
      list.push({
        id: 'order',
        label: 'Layers',
        icon: IconNames.LayersRegular,
        onClick: () => openInlineTool('order'),
      });
    }
    if (isPodProductCode && !shouldShowReplace) {
      list.push({ id: 'delete', label: 'Delete', icon: IconNames.Trash, onClick: onDelete });
    }
    if (!isHandwriting && isUS && shouldShowReplace) {
      list.push({ id: 'replace', label: 'Replace', icon: IconNames.Camera, onClick: openReplaceConfirmation });
    }
    if (isSignAndSend) {
      list.push({ id: 'reset', label: 'Reset', icon: IconNames.Trash, onClick: onReset });
    }
    return list;
  }, [
    isCropping,
    isHandwriting,
    isPhotoZoneImage,
    isPodProductCode,
    isSignAndSend,
    isUS,
    onCrop,
    onDelete,
    onReset,
    openInlineTool,
    openReplaceConfirmation,
    setActiveTool,
    shouldShowReplace,
  ]);

  // Restore selection synchronously, *before* the inline tool body mounts,
  // so child hooks (usePhotoControls) capture the correct active object on
  // their first render. useLayoutEffect runs before the browser paints, so
  // the restoration happens between the parent render and child mount
  // without performing a side effect inside render itself.
  useLayoutEffect(() => {
    if (activeTool) {
      restoreCachedSelection();
    }
  }, [activeTool, restoreCachedSelection]);

  const inlineBody = activeTool ? <InlineToolBody tool={activeTool} /> : null;

  if (!isVisible && !isDesktopRendered) {
    return null;
  }

  const confirmationDialog = (
    <ConfirmationDialog
      id="image-edit-toolbar-replace-confirmation"
      testId="image-edit-toolbar-replace-confirmation"
      title={labels.replaceTitle}
      cancelLabel={labels.replaceCancel}
      confirmLabel={labels.replaceConfirm}
      isOpen={isReplaceConfirmationOpen}
      onCancel={closeReplaceConfirmation}
      onConfirm={confirmReplaceImage}
      accentIcon={IconNames.CardFeaturesBold}
    >
      {null}
    </ConfirmationDialog>
  );

  // In a sub-panel, show the label of the tool being edited (Zoom / Rotate
  // / Layers / Crop). In the root state, show the panel-level title.
  // 'crop' needs a special case because while `isCropping` is true the
  // `actions` list is intentionally empty (see the early return in the
  // useMemo above), so `actions.find(...)` would return undefined.
  const resolvedTitle = (() => {
    if (activeTool === 'crop') return 'Crop';
    if (activeTool) return actions.find((action) => action.id === activeTool)?.label ?? 'Edit photo';
    return 'Edit photo';
  })();

  const sharedMenuProps = {
    ariaLabel: labels.toolbar,
    backLabel: labels.back,
    closeLabel: labels.close,
    onBack: handleBack,
    onClose: handleClose,
    showBack: !!activeTool,
    title: resolvedTitle,
    testIdPrefix: TOOLBAR_TEST_ID_PREFIX,
    actions,
  };

  // Mobile: render embedded inside a bottom-anchored Drawer so the menu
  // sits at the bottom of the viewport like the legacy ImageEditDrawer did.
  if (isMobile) {
    if (!isVisible) {
      return null;
    }
    return (
      <>
        {confirmationDialog}
        <Drawer
          drawerPosition={DrawerPositions.Bottom}
          isOpen={isVisible}
          onClose={handleClose}
          hideCloseButton
          showVeil={false}
          ariaLabel={labels.toolbar}
          drawerTestId="image-edit-toolbar-mobile"
        >
          <ToolbarActionMenu {...sharedMenuProps} variant="embedded">
            {inlineBody}
          </ToolbarActionMenu>
        </Drawer>
      </>
    );
  }

  // Desktop: render via portal into the editor's host element so the
  // floating panel sits above the canvas.
  const host = getImageEditToolbarHost();
  if (!host || !isDesktopRendered) {
    return null;
  }

  return (
    <>
      {confirmationDialog}
      {createPortal(
        <ToolbarActionMenu
          {...sharedMenuProps}
          variant="floating"
          isClosing={isDesktopClosing}
          onExitComplete={onDesktopExitComplete}
        >
          {inlineBody}
        </ToolbarActionMenu>,
        host,
      )}
    </>
  );
};
